export interface EntityKeyByReference {
  reference: string;
  entityDefinitionCode: number;
}

// Numeric enums — values must match Sierra.Finance.Models.Enums (declaration order).
export enum BankAccountType {
  CHECKING = 0,
  SAVINGS = 1,
  PAYROLL = 2,
  FIXED_TERM = 3,
}

export enum BankAccountStatus {
  ACTIVE = 0,
  BLOCKED = 1,
  INACTIVE = 2,
}

export enum Currency {
  USD = 0,
  HNL = 1,
  EUR = 2,
}

/** Mirrors Sierra.Finance.Models.Enums.BankAccountAccessRight (declaration order). */
export enum BankAccountAccessRight {
  VIEW_BALANCE = 0,
  VIEW_TRANSACTIONS = 1,
  CREATE_TRANSACTION = 2,
  POST_TRANSACTION = 3,
  RECONCILE = 4,
  FULL_ACCESS = 5,
}

/** One employee's grant on one account. Keyed by username — see BankAccountEmployeeAccess (API). */
export interface BankAccountEmployeeAccess {
  username: string;
  access: BankAccountAccessRight[];
}

/**
 * Payload for PUT /bank-accounts/{id}/access. Owner and employees travel together because they are
 * one decision — see BankAccountAccessUpdate (API).
 */
export interface BankAccountAccessUpdate {
  ownerUsername: string;
  /** REPLACES the stored list — an omitted employee is a revoked employee. */
  employeeAccess: BankAccountEmployeeAccess[];
}

/** A user who can be named as owner or granted access. From GET /bank-accounts/assignable-users. */
export interface BankAccountAssignableUser {
  username: string;
  displayName: string;
  email?: string | null;
}

export interface BankAccount {
  id?: string;
  code?: string | null;
  name: string;
  bankName: string;
  bankAccountNumber: string;
  type: number;
  currency: number;
  status: number;
  country: string;
  swiftBicCode?: string | null;
  ibanCode?: string | null;
  glAccountReference?: EntityKeyByReference | null;
  glAccountNumber?: string | null;
  financialCompanyReference: EntityKeyByReference;
  accountHolderName: string;
  holderPhoneNumber?: string | null;
  description?: string | null;
  /**
   * Balances are `null` when the API has REDACTED them for the current user — not when they are zero.
   * Never coalesce to 0 for display: render the mask (`| maskedAmount`) and leave totals out entirely,
   * or a withheld figure becomes a stated one.
   */
  initialBalance: number | null;
  initialBalanceDate: string;
  currentBalance: number | null;
  minimumBalance: number | null;
  maximumBalance: number | null;

  /** The sole user who may edit this account and manage its access list. Fixed at creation. */
  ownerUsername?: string | null;
  /** Populated only for the owner; empty for everyone else. */
  employeeAccess: BankAccountEmployeeAccess[];

  // Answered by the API per requesting user — don't re-derive these from the two fields above.
  canViewBalance: boolean;
  canCreateTransaction: boolean;
  isOwner: boolean;

  createdDate?: string;
  createdBy?: string;
  modifiedDate?: string;
  modifiedBy?: string;
}
